import { type Placement } from '@floating-ui/dom';

export type PopoverTriggerType = 'mouseenter' | 'click' | 'manual';

/**
 * A popover variation: a named preset of placement, trigger and styling. The
 * `theme` maps to the `data-theme` attribute consumed by the tooltip theme SCSS.
 */
export interface PopoverVariation {
  theme: string;
  arrow: boolean;
  animation: 'shift-away' | false;
  trigger: PopoverTriggerType;
  /** [skidding (cross axis), distance (main axis)] in px. */
  offset: [number, number];
  placement: Placement;
  interactive: boolean;
  followCursor: 'horizontal' | false;
}

const tooltip: PopoverVariation = {
  theme: 'default',
  arrow: true,
  animation: 'shift-away',
  trigger: 'mouseenter',
  offset: [0, 10],
  placement: 'bottom-start',
  interactive: false,
  followCursor: false,
};

const tooltipFollow: PopoverVariation = {
  ...tooltip,
  followCursor: 'horizontal',
};

const menu: PopoverVariation = {
  ...tooltip,
  trigger: 'click',
  arrow: false,
  theme: 'menu',
  interactive: true,
};

const inputDropdown: PopoverVariation = {
  ...menu,
  theme: 'input-dropdown',
};

const popoutLarge: PopoverVariation = {
  ...tooltip,
  theme: 'popout-large',
  interactive: true,
};

const date: PopoverVariation = {
  ...popoutLarge,
  theme: 'date',
};

/**
 * Registry keyed by the directive `rcpPopoverVariation` name. The `*OverModal`
 * entries share config with their base but keep a distinct key so the
 * `.rcp-popover-variation-<name>` class can raise z-index over modals (tooltip SCSS).
 */
export const POPOVER_VARIATIONS = {
  followX: tooltipFollow,
  default: tooltip,
  defaultOverModal: tooltip,
  menu,
  menuOverModal: menu,
  inputDropdown,
  inputDropdownOverModal: inputDropdown,
  popoutLarge,
  popoutLargeOverModal: popoutLarge,
  date,
  dateOverModal: date,
} satisfies Record<string, PopoverVariation>;

export type PopoverVariationName = keyof typeof POPOVER_VARIATIONS;
